// Persists an in-progress application in localStorage, keyed by joboffer id, so
// it can be restored when the candidate returns to the same job's apply form
// (e.g. after leaving to register an account). Keying by joboffer id ensures a
// draft is never restored onto a different job's form.

const KEY_PREFIX = 'hp_application_draft_';

function storageKey(jobofferId) {
  return `${KEY_PREFIX}${jobofferId}`;
}

export function saveApplicationDraft(jobofferId, application) {
  if (!jobofferId || !application) {
    return;
  }

  try {
    localStorage.setItem(storageKey(jobofferId), JSON.stringify(application));
  } catch (e) {
    // Storage unavailable (private mode / quota) — draft just won't persist.
  }
}

function isEmptyValue(value) {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}

// Merges a saved draft onto the current application, copying only the draft's
// non-empty values. An empty field in the draft therefore never wipes data that
// is already present on the application (e.g. values pre-filled from the
// candidate's profile). Returns a new object; the inputs are not mutated.
export function mergeApplicationDraft(application, draft) {
  if (!draft) {
    return application;
  }

  const merged = { ...application };
  Object.keys(draft).forEach((key) => {
    if (!isEmptyValue(draft[key])) {
      merged[key] = draft[key];
    }
  });

  return merged;
}

export function loadApplicationDraft(jobofferId) {
  if (!jobofferId) {
    return null;
  }

  try {
    const raw = localStorage.getItem(storageKey(jobofferId));
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

export function clearApplicationDraft(jobofferId) {
  if (!jobofferId) {
    return;
  }

  try {
    localStorage.removeItem(storageKey(jobofferId));
  } catch (e) {
    // ignore
  }
}

// Returns the joboffer id of a stored, in-progress application draft (the first
// one found), or null if there is none. Used after account confirmation to offer
// a "return to your application" shortcut back to the right apply form.
export function getStoredApplicationDraftJobofferId() {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(KEY_PREFIX)) {
        return key.slice(KEY_PREFIX.length);
      }
    }
  } catch (e) {
    // Storage unavailable — treat as no draft.
  }

  return null;
}
